import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../../singleton/axios';
import { Watchlist } from './useWatchlists';

interface AddStockData {
  ticker: string;
}

interface AddStockResponse {
  data: {
    id: string;
    watchlist_id: string;
    ticker: string;
    created_at: string;
    updated_at: string;
  };
}

async function addStockToWatchlist(watchlistId: string, requestData: AddStockData): Promise<AddStockResponse['data']> {
  try {
    const { data } = await api.post<AddStockResponse>(`/stock-watchlists/${watchlistId}/holdings`, requestData);
    return data.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
      `Failed to add stock to watchlist: ${error.response?.status || 'Unknown error'}`
    );
  }
}

export function useAddStockToWatchlist(watchlistId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddStockData) => addStockToWatchlist(watchlistId, data),

    // Optimistic update - immediately add stock to cache before API call completes
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['watchlist', watchlistId] });

      // Snapshot the previous value for rollback
      const previousWatchlist = queryClient.getQueryData<Watchlist>(['watchlist', watchlistId]);

      // Optimistically update the cache
      queryClient.setQueryData<Watchlist>(['watchlist', watchlistId], (old) => {
        if (!old) return old;

        // Create optimistic holding with temporary ID
        const optimisticHolding = {
          id: `temp-${Date.now()}`, // Temporary ID until server confirms
          ticker: variables.ticker,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        return {
          ...old,
          holdings: [...old.holdings, optimisticHolding],
          holdings_count: old.holdings_count + 1,
        };
      });

      // Return context with previous value for rollback
      return { previousWatchlist };
    },

    onSuccess: (newHolding, variables) => {
      // Invalidate queries to get fresh data from server (confirming the optimistic update)
      queryClient.invalidateQueries({ queryKey: ['watchlist', watchlistId] });
      queryClient.invalidateQueries({ queryKey: ['watchlists'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-watchlist'] }); // Update dashboard too
      // The earnings screen reads the same holdings. Without this, bookmarking
      // into the list it is bound to leaves the company absent there for the
      // full staleTime while the switcher's count has already moved.
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['chartsWatchlist'] }); // Update charts page watchlist

      // Invalidate and refetch the company quote for the newly added ticker
      // This ensures price data loads immediately in WatchlistPriceTile
      queryClient.invalidateQueries({ queryKey: ['companyQuote', variables.ticker] });
      queryClient.refetchQueries({ queryKey: ['companyQuote', variables.ticker] });

      // Show success notification
      enqueueSnackbar(`${variables.ticker} added to watchlist successfully!`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    },

    onError: (error, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousWatchlist) {
        queryClient.setQueryData(['watchlist', watchlistId], context.previousWatchlist);
      }

      // Show error notification
      enqueueSnackbar(error.message || 'Failed to add stock to watchlist', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });
}